import React, { useState, useRef } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { CartItem } from '../types';

interface CheckoutOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  totalPrice: number;
  onOrderSuccess: (customerData?: { name: string, address: string, phone: string, orderNotes: string, deliveryNotes: string, paymentMethod: 'cash' | 'digital' }) => void;
  orderNotes: string;
}

const CheckoutOverlay: React.FC<CheckoutOverlayProps> = ({ isOpen, onClose, cartItems, totalPrice, onOrderSuccess, orderNotes }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<'info' | 'payment' | 'confirm'>('info');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'digital'>('cash');
  const [walletType, setWalletType] = useState<'venmo' | 'cashapp' | 'zelle'>('venmo');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', address: '', deliveryNotes: '' });

  const WALLET_IDS = {
    venmo: '@GustoPizzeria',
    cashapp: '$GustoTally',
    zelle: 'pay@gustostudio.com'
  };

  const DISCORD_WEBHOOK = "https://discord.com/api/webhooks/1465951576081563680/N5bnKcJ48EphqBNunpNHLMrsoWhS-XqYXxP65v5ee98N2wSf0WuH40rgghusrtOYO3e9"; 

  useGSAP(() => {
    if (isOpen) {
      gsap.to(containerRef.current, {
        y: 0,
        duration: 0.8,
        ease: "expo.out"
      });
    } else {
      gsap.to(containerRef.current, {
        y: '100%',
        duration: 0.6,
        ease: "power4.in"
      });
    }
  }, [isOpen]);

  const getItemUnitPrice = (item: CartItem): number => {
    let base = parseInt(item.price.replace('$', '')) || 0;
    if (item.customization?.crust) {
      if (item.customization.crust.includes('+$1.50')) base += 1.5;
      if (item.customization.crust.includes('+$2.00') || item.customization.crust.includes('+$2')) base += 2;
    }
    if (item.customization?.extraToppings) {
      item.customization.extraToppings.forEach(t => {
        base += t.price;
      });
    }
    return base;
  };

  const handleDeepLink = (type: 'venmo' | 'cashapp' | 'zelle') => {
    setWalletType(type);
    const amount = totalPrice.toFixed(2);
    const note = `Order for ${formData.name || 'Gusto Guest'}`;
    
    let url = '';
    if (type === 'venmo') {
      url = `venmo://paycharge?txn=pay&recipients=${WALLET_IDS.venmo}&amount=${amount}&notes=${encodeURIComponent(note)}`;
    } else if (type === 'cashapp') {
      url = `https://cash.app/${WALLET_IDS.cashapp}/${amount}`;
    }

    if (url) {
      window.location.href = url;
    } else {
      navigator.clipboard.writeText(WALLET_IDS[type]);
      alert(`${type.toUpperCase()} ID Copied: ${WALLET_IDS[type]}`);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshot(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const submitOrder = async () => {
    if ("Notification" in window) {
      try {
        await Notification.requestPermission();
      } catch (err) {
        console.warn("Notification request failed:", err);
      }
    }

    setIsSubmitting(true);
    
    const itemList = cartItems.map(i => {
      const unitPrice = getItemUnitPrice(i);
      let line = `• **${i.quantity}x** ${i.name} ($${(unitPrice * i.quantity).toFixed(2)})`;
      if (i.customization?.crust && i.customization.crust !== '72-Hour Standard Sourdough') {
        line += `\n   └ Crust: ${i.customization.crust}`;
      }
      if (i.customization?.extraToppings && i.customization.extraToppings.length > 0) {
        line += `\n   └ Toppings: ${i.customization.extraToppings.map(t => t.name).join(', ')}`;
      }
      return line;
    }).join('\n');

    const orderId = Math.random().toString(36).substring(2, 8).toUpperCase();

    const orderData = {
      items: cartItems.map(i => ({ 
        id: i.id, 
        qty: i.quantity,
        customization: i.customization
      })),
      total: totalPrice,
      customer: formData.name,
      address: formData.address,
      phone: formData.phone,
      orderNotes: orderNotes,
      deliveryNotes: formData.deliveryNotes,
      paymentMethod: paymentMethod,
      time: Date.now()
    };
    const printLink = `${window.location.origin}${window.location.pathname}?order=${btoa(JSON.stringify(orderData))}`;

    const payload = {
      content: `### 🚨 NEW GUSTO ORDER: #${orderId}`,
      embeds: [{
        title: "🔥 Kitchen Ticket - Ready to Forge",
        description: `**ID:** #${orderId}\n**Customer:** ${formData.name}\n**Phone:** ${formData.phone}`,
        color: 14703160, 
        fields: [
          { 
            name: "📍 Delivery Address", 
            value: `\`\`\`\n${formData.address}\n\`\`\`` 
          },
          { 
            name: "🍕 Items & Customizations", 
            value: itemList || "No items listed"
          },
          { 
            name: "📝 Notes", 
            value: `**Order:** ${orderNotes || "None"}\n**Delivery:** ${formData.deliveryNotes || "None"}`
          },
          { 
            name: "💰 Total & Payment", 
            value: `**Total:** $${totalPrice}\n**Method:** ${paymentMethod === 'cash' ? '💵 Cash on Delivery' : '📱 Digital Transfer'}`, 
            inline: true 
          },
          {
            name: "🖨️ Kitchen Tools",
            value: `[**PRINT TICKET FOR RIDER**](${printLink})`,
            inline: true
          }
        ],
        footer: { 
          text: "GUSTO | Sculpted by Heat. Defined by Gravity.",
          icon_url: "https://grainy-gradients.vercel.app/noise.svg"
        },
        timestamp: new Date().toISOString()
      }]
    };

    const discordFormData = new FormData();
    discordFormData.append('payload_json', JSON.stringify(payload));

    if (screenshot) {
      try {
        const fetchRes = await fetch(screenshot);
        const blob = await fetchRes.blob();
        discordFormData.append('files[0]', blob, 'proof.png');
        const updatedPayload = {
          ...payload,
          embeds: [{
            ...payload.embeds[0],
            image: { url: "attachment://proof.png" }
          }]
        };
        discordFormData.set('payload_json', JSON.stringify(updatedPayload));
      } catch (err) {
        console.error("Failed to attach image:", err);
      }
    }

    try {
      await fetch(DISCORD_WEBHOOK, {
        method: 'POST',
        body: discordFormData,
      });
      onOrderSuccess({ 
        name: formData.name, 
        address: formData.address, 
        phone: formData.phone,
        orderNotes: orderNotes,
        deliveryNotes: formData.deliveryNotes,
        paymentMethod: paymentMethod
      });
    } catch (error) {
      console.error("Discord post failed:", error);
      onOrderSuccess({ 
        name: formData.name, 
        address: formData.address, 
        phone: formData.phone,
        orderNotes: orderNotes,
        deliveryNotes: formData.deliveryNotes,
        paymentMethod: paymentMethod
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 z-[300] bg-[#F2E9D8] transform translate-y-full flex flex-col h-[100dvh] w-screen overflow-hidden"
    >
      <div className="p-4 md:p-6 lg:p-8 flex justify-between items-center border-b-2 md:border-b-4 border-[#1A3C28] flex-shrink-0 bg-[#F2E9D8] z-10">
        <h2 className="font-display text-[clamp(2rem,6vw,4rem)] font-black uppercase tracking-tighter text-[#1A3C28] leading-none">
          Check<span className="text-[#E05A38]">out</span>
        </h2>
        <button onClick={onClose} className="p-2 md:p-3 bg-[#1A3C28] text-[#F2E9D8] rounded-full hover:bg-[#E05A38] transition-colors shadow-lg">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>

      <div 
        className="flex-1 overflow-y-auto min-h-0 bg-[#F2E9D8] overscroll-behavior-contain scrollbar-hide"
        data-lenis-prevent
      >
        <div className="max-w-2xl mx-auto px-6 py-8 md:py-10 space-y-10 md:space-y-12">
          
          {/* Order Summary Box */}
          <div className="bg-[#1A3C28]/5 border-2 border-[#1A3C28]/15 p-5 md:p-6 rounded-[1.5rem] space-y-4">
            <div className="flex justify-between items-center border-b border-[#1A3C28]/10 pb-3">
              <span className="font-black uppercase text-[10px] tracking-[0.3em] text-[#1A3C28]/60">
                Order Items ({cartItems.reduce((sum, i) => sum + i.quantity, 0)})
              </span>
              <span className="font-display font-black text-xl text-[#1A3C28]">
                ${totalPrice.toFixed(2)}
              </span>
            </div>

            <div className="space-y-3 max-h-48 overflow-y-auto pr-1 scrollbar-hide">
              {cartItems.map(item => {
                const unitPrice = getItemUnitPrice(item);
                return (
                  <div key={item.id} className="flex justify-between items-start text-xs border-b border-[#1A3C28]/5 pb-2.5">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-black text-white bg-[#1A3C28] px-2 py-0.5 rounded-md text-[9px]">
                          {item.quantity}x
                        </span>
                        <span className="font-black text-[#1A3C28] uppercase">{item.name}</span>
                      </div>
                      {item.customization?.crust && item.customization.crust !== '72-Hour Standard Sourdough' && (
                        <p className="text-[10px] text-[#1A3C28]/70 font-semibold mt-1 pl-6">
                          • Crust: {item.customization.crust.split('(')[0].trim()}
                        </p>
                      )}
                      {item.customization?.extraToppings && item.customization.extraToppings.length > 0 && (
                        <p className="text-[10px] text-[#E05A38] font-bold mt-0.5 pl-6">
                          • +{item.customization.extraToppings.map(t => t.name).join(', ')}
                        </p>
                      )}
                    </div>
                    <span className="font-display font-black text-[#1A3C28]">
                      ${(unitPrice * item.quantity).toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`space-y-6 transition-opacity duration-500 ${step === 'info' ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
            <h3 className="font-black uppercase text-[10px] tracking-[0.4em] text-[#1A3C28]/40 flex items-center gap-4">
              <span className="w-8 h-8 rounded-full bg-[#1A3C28] text-white flex items-center justify-center text-[10px]">01</span>
              Delivery Details
            </h3>
            <div className="grid gap-4 md:gap-6">
              <input 
                type="text" placeholder="FULL NAME"
                className="w-full bg-transparent border-b-2 border-[#1A3C28] p-2 md:p-4 font-black uppercase tracking-widest text-lg md:text-xl focus:outline-none focus:border-[#E05A38] transition-colors placeholder:opacity-20"
                value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
              />
              <input 
                type="tel" placeholder="PHONE NUMBER"
                className="w-full bg-transparent border-b-2 border-[#1A3C28] p-2 md:p-4 font-black uppercase tracking-widest text-lg md:text-xl focus:outline-none focus:border-[#E05A38] transition-colors placeholder:opacity-20"
                value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
              />
              <textarea 
                placeholder="DELIVERY ADDRESS" rows={2}
                className="w-full bg-transparent border-b-2 border-[#1A3C28] p-2 md:p-4 font-black uppercase tracking-widest text-lg md:text-xl focus:outline-none focus:border-[#E05A38] transition-colors resize-none placeholder:opacity-20"
                value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})}
              />
              <textarea 
                placeholder="DELIVERY NOTES (Door codes, instructions...)" rows={2}
                className="w-full bg-transparent border-b-2 border-[#1A3C28] p-2 md:p-4 font-black uppercase tracking-widest text-lg md:text-xl focus:outline-none focus:border-[#E05A38] transition-colors resize-none placeholder:opacity-20"
                value={formData.deliveryNotes} onChange={e => setFormData({...formData, deliveryNotes: e.target.value})}
              />
            </div>
            {step === 'info' && (
              <button 
                onClick={() => setStep('payment')}
                disabled={!formData.name || !formData.phone || !formData.address}
                className="w-full bg-[#1A3C28] text-white py-4 md:py-6 rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] md:text-xs hover:scale-[1.01] active:scale-95 transition-all shadow-xl disabled:opacity-20"
              >
                Proceed to Payment
              </button>
            )}
          </div>

          <div className={`space-y-8 transition-opacity duration-500 ${step === 'payment' ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
            <h3 className="font-black uppercase text-[10px] tracking-[0.4em] text-[#1A3C28]/40 flex items-center gap-4">
              <span className="w-8 h-8 rounded-full bg-[#1A3C28] text-white flex items-center justify-center text-[10px]">02</span>
              Payment Method
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => setPaymentMethod('cash')}
                className={`p-4 md:p-6 border-2 rounded-[1.5rem] flex flex-col items-center gap-2 transition-all ${paymentMethod === 'cash' ? 'border-[#E05A38] bg-[#E05A38]/5' : 'border-[#1A3C28]/10 opacity-50 hover:opacity-100'}`}
              >
                <span className="text-xl md:text-3xl">💵</span>
                <span className="font-black uppercase text-[8px] md:text-[9px] tracking-widest text-center leading-tight">Cash on Delivery</span>
              </button>
              <button 
                onClick={() => setPaymentMethod('digital')}
                className={`p-4 md:p-6 border-2 rounded-[1.5rem] flex flex-col items-center gap-2 transition-all ${paymentMethod === 'digital' ? 'border-[#E05A38] bg-[#E05A38]/5' : 'border-[#1A3C28]/10 opacity-50 hover:opacity-100'}`}
              >
                <span className="text-xl md:text-3xl">📱</span>
                <span className="font-black uppercase text-[8px] md:text-[9px] tracking-widest text-center leading-tight">Digital Transfer</span>
              </button>
            </div>

            {paymentMethod === 'digital' && (
              <div className="space-y-6 bg-white/40 p-5 md:p-6 rounded-[1.5rem] border border-[#1A3C28]/5 animate-in fade-in slide-in-from-bottom-6">
                <div className="space-y-4">
                  <p className="font-black uppercase text-[8px] tracking-[0.2em] text-center opacity-40">Select Platform</p>
                  <div className="flex flex-wrap justify-center gap-4">
                    {/* Cash App Icon (Sequence 1) */}
                    <button 
                      onClick={() => handleDeepLink('cashapp')} 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md transition-transform active:scale-90 bg-white group border border-transparent hover:border-[#00d632]/20"
                      title="Cash App"
                    >
                      <svg fill="#00d632" className="w-8 h-8 md:w-10 md:h-10" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                        <path d="M31.453 4.625c-0.688-1.891-2.177-3.375-4.068-4.063-1.745-0.563-3.333-0.563-6.557-0.563h-9.682c-3.198 0-4.813 0-6.531 0.531-1.896 0.693-3.385 2.188-4.068 4.083-0.547 1.734-0.547 3.333-0.547 6.531v9.693c0 3.214 0 4.802 0.531 6.536 0.688 1.891 2.177 3.375 4.068 4.063 1.734 0.547 3.333 0.547 6.536 0.547h9.703c3.214 0 4.813 0 6.536-0.531 1.896-0.688 3.391-2.182 4.078-4.078 0.547-1.734 0.547-3.333 0.547-6.536v-9.667c0-3.214 0-4.813-0.547-6.547zM23.229 10.802l-1.245 1.24c-0.25 0.229-0.635 0.234-0.891 0.010-1.203-1.010-2.724-1.568-4.292-1.573-1.297 0-2.589 0.427-2.589 1.615 0 1.198 1.385 1.599 2.984 2.198 2.802 0.938 5.12 2.109 5.12 4.854 0 2.99-2.318 5.042-6.104 5.266l-0.349 1.604c-0.063 0.302-0.328 0.516-0.635 0.516h-2.391l-0.12-0.010c-0.354-0.078-0.578-0.432-0.505-0.786l0.375-1.693c-1.438-0.359-2.76-1.083-3.844-2.094v-0.016c-0.25-0.25-0.25-0.656 0-0.906l1.333-1.292c0.255-0.234 0.646-0.234 0.896 0 1.214 1.146 2.839 1.786 4.521 1.76 1.734 0 2.891-0.734 2.891-1.896s-1.172-1.464-3.385-2.292c-2.349-0.839-4.573-2.026-4.573-4.802 0-3.224 2.677-4.797 5.854-4.943l0.333-1.641c0.063-0.302 0.333-0.516 0.641-0.51h2.37l0.135 0.016c0.344 0.078 0.573 0.411 0.495 0.76l-0.359 1.828c1.198 0.396 2.333 1.026 3.302 1.849l0.031 0.031c0.25 0.266 0.25 0.667 0 0.906z"/>
                      </svg>
                    </button>

                    {/* Venmo Icon (Sequence 2) */}
                    <button 
                      onClick={() => handleDeepLink('venmo')} 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md transition-transform active:scale-90 bg-white overflow-hidden border border-transparent hover:border-[#008cff]/20"
                      title="Venmo"
                    >
                      <svg viewBox="0 0 512 512" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#008cff" d="M25.329 25.334h461.332v461.332H25.33z"/>
                        <path d="M364.178 131.79c8.97 14.762 13.013 29.966 13.013 49.172 0 61.26-52.48 140.84-95.074 196.72h-97.285l-39.016-232.48 85.185-8.06 20.627 165.426c19.275-31.29 43.062-80.464 43.062-113.989 0-18.35-3.155-30.85-8.085-41.142l77.573-15.648z" fill="#fff"/>
                      </svg>
                    </button>

                    {/* Zelle Icon (Sequence 3) */}
                    <button 
                      onClick={() => handleDeepLink('zelle')} 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md transition-transform active:scale-90 bg-white border border-transparent hover:border-[#6D1ED4]/20"
                      title="Zelle"
                    >
                      <svg viewBox="0 0 59.1 100" className="h-9" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#6D1ED4" d="M58.7,83.4c-0.1-0.1-0.1-0.1-0.2-0.2l-0.1-0.1c-2.7-2.8-4.8-6.1-6.4-9.8c-0.2-0.6-0.8-1-1.4-1h-1.3h-5.8H20.6
                        l36.6-46.9c0.2-0.3,0.3-0.6,0.3-1v-9.1c0-0.9-0.7-1.6-1.6-1.6H38.8h-0.7h-2V0.8c0-0.4-0.4-0.8-0.8-0.8H35h-0.3H23.9h-0.4h-0.3
                        c-0.4,0-0.8,0.4-0.8,0.8v13.1h-2.8H19H3.1c-0.9,0-1.6,0.7-1.6,1.6V26c0,0.9,0.7,1.6,1.6,1.6h33.6L0.3,74C0.1,74.3,0,74.7,0,75v9.5
                        c0,0.9,0.7,1.6,1.6,1.6h20.9v13.1c0,0.4,0.4,0.8,0.8,0.8h0.3H24h10.8H35h0.4c0.4,0,0.8-0.4,0.8-0.8V86.1H45h11.3h1.3
                        c0.6,0,1.2-0.4,1.4-1C59.3,84.6,59.1,83.9,58.7,83.4">
                        </path>
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-[#1A3C28]/10 text-center">
                  <p className="font-black uppercase text-[8px] tracking-[0.2em] mb-3 opacity-40">Upload Proof</p>
                  <label className="relative inline-block group cursor-pointer w-full">
                    <div className="border-2 border-dashed border-[#1A3C28]/20 rounded-[1.25rem] p-4 flex flex-col items-center justify-center gap-2 group-hover:border-[#E05A38]/40 transition-colors bg-[#F2E9D8]/30 min-h-[120px]">
                      {screenshot ? (
                        <div className="relative w-full flex flex-col items-center gap-2">
                           <img src={screenshot} className="h-24 md:h-32 rounded-lg object-contain shadow-md" alt="Screenshot" />
                           <div className="bg-[#1A3C28] text-white px-3 py-1 rounded-full text-[7px] font-black uppercase tracking-widest">Image Attached</div>
                        </div>
                      ) : (
                        <>
                          {/* Theme-matched animated Camera Icon */}
                          <svg viewBox="0 0 24 24" className="w-10 h-10 animate-pulse text-[#E05A38]" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                            <circle cx="12" cy="13" r="4"></circle>
                          </svg>
                          <span className="font-black uppercase text-[8px] tracking-widest opacity-40">Tap to upload proof</span>
                        </>
                      )}
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                  </label>
                </div>
              </div>
            )}

            {step === 'payment' && (
              <div className="flex gap-3 pt-4">
                <button onClick={() => setStep('info')} className="flex-1 border-2 border-[#1A3C28] text-[#1A3C28] py-4 rounded-xl font-black uppercase tracking-[0.2em] text-[9px]">Back</button>
                <button 
                  onClick={submitOrder}
                  disabled={isSubmitting || (paymentMethod === 'digital' && !screenshot)}
                  className="flex-[2] bg-[#E05A38] text-white py-4 rounded-xl font-black uppercase tracking-[0.2em] text-[9px] shadow-xl disabled:opacity-20"
                >
                  {isSubmitting ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    "Confirm Order"
                  )}
                </button>
              </div>
            )}
          </div>
          
          <div className="h-16 md:h-24"></div>
        </div>
      </div>

      <div className="fixed bottom-4 right-4 md:bottom-6 md:right-8 z-[310] pointer-events-none no-print">
        <div className="bg-[#1A3C28] text-[#F2E9D8] px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-4 border border-white/10 animate-in fade-in slide-in-from-right-10 pointer-events-auto">
          <div className="flex flex-col">
            <p className="font-black uppercase text-[7px] tracking-[0.3em] opacity-40 leading-none mb-1">Total Due</p>
            <div className="flex items-start gap-0.5">
               <span className="font-display text-sm font-black mt-1">$</span>
               <p className="font-display text-2xl md:text-3xl font-black tracking-tighter leading-none">{totalPrice.toFixed(2)}</p>
            </div>
          </div>
          <div className="w-px h-8 bg-white/10 mx-1"></div>
          <div className="text-right flex flex-col justify-center">
            <p className="font-black uppercase text-[6px] tracking-[0.3em] opacity-30 leading-none">STUDIO</p>
            <p className="font-black uppercase text-[6px] tracking-[0.3em] opacity-30 leading-none mt-1">GUSTO</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutOverlay;
